/**
 * Mega Levels and Mega forms (2026-09-03 owner ask).
 *
 * WHAT A LEVEL IS. In GO a Mega Level belongs to ONE Pokémon, not the
 * species: it climbs with the number of times THAT Pokémon has Mega Evolved
 * (1 for Base, 7 for High, 30 for Max; Super Max is the 2026 tier that only
 * designated Pokémon can reach). The trainer may own a Max-level Charizard
 * and still Mega Evolve a different Charizard sitting at Base, which is why
 * every level stays selectable everywhere: the level is what they say it
 * is, never something the app locks by what it thinks they own.
 *
 * WHAT THE APP TRACKS. Per Mega FORM (Charizard X and Y are two forms), the
 * highest level the trainer says they have reached (megaLevelStore). And
 * the ONE Mega active right now, with the level they evolved at and an
 * eight-hour clock, because that is how long a Mega Evolution lasts.
 *
 * Perk figures from Niantic's Mega Level table as reported by
 * pokemon.com, the Pokémon GO wiki and the 2026 Super Max coverage; see the
 * sources in the PR that added this. Edit here if the game changes them.
 */
import {
  MEGA_EXTRA_FORMS,
  megaFormLabel,
  megaGmaxSlug,
  megaRosterBaseIds,
} from "./megaGmaxSprites";
import { MEGA_STATS, megaStatFor, type MegaStat } from "./generated/megaStats.generated";
import { speciesName } from "../data/speciesNames";

export type MegaLevel = 1 | 2 | 3 | 4;

export interface MegaLevelInfo {
  level: MegaLevel;
  key: "base" | "high" | "max" | "superMax";
  label: string;
  /** Mega Evolutions of that Pokémon needed to reach it (Super Max: designated only). */
  evolutionsToReach: number | null;
  /** Rest period before the same Pokémon can Mega Evolve again without energy. */
  restHours: number;
  /** Extra Candy per catch of a Pokémon sharing a type with the Mega. */
  catchCandy: number;
  /** Extra chance of Candy XL on those catches, percentage points. */
  xlChancePct: number;
  /** Extra XP per catch of those Pokémon. */
  catchXp: number;
}

export const MEGA_LEVELS: readonly MegaLevelInfo[] = [
  { level: 1, key: "base", label: "Base", evolutionsToReach: 1, restHours: 7 * 24, catchCandy: 1, xlChancePct: 0, catchXp: 0 },
  { level: 2, key: "high", label: "High", evolutionsToReach: 7, restHours: 5 * 24, catchCandy: 1, xlChancePct: 10, catchXp: 50 },
  { level: 3, key: "max", label: "Max", evolutionsToReach: 30, restHours: 3 * 24, catchCandy: 2, xlChancePct: 25, catchXp: 100 },
  { level: 4, key: "superMax", label: "Super Max", evolutionsToReach: null, restHours: 24, catchCandy: 3, xlChancePct: 30, catchXp: 200 },
];

export function megaLevelInfo(level: MegaLevel): MegaLevelInfo {
  return MEGA_LEVELS[level - 1];
}

/** The level after this one that evolutions can reach (Super Max is designated only), or null. */
export function megaNextLevel(level: MegaLevel | null): MegaLevelInfo | null {
  const next = MEGA_LEVELS.find((l) => l.level === (level ?? 0) + 1);
  return next && next.evolutionsToReach != null ? next : null;
}

/**
 * Where a form stands toward its next level, from what is known: the total
 * evolutions when a page said so ("Mega Evolve Mewtwo 4 more times" makes
 * 3 of 7), else the fraction the level circles showed. `done` and `needed`
 * are null when nothing is known beyond the level.
 */
export function megaLevelProgress(entry: {
  level: MegaLevel | null;
  evolutions?: number;
  progressToNext?: number;
}): { next: MegaLevelInfo | null; done: number | null; needed: number | null; fraction: number | null } {
  const next = megaNextLevel(entry.level);
  if (!next) return { next: null, done: null, needed: null, fraction: null };
  const needed = next.evolutionsToReach!;
  if (entry.evolutions != null) {
    const done = Math.max(0, Math.min(needed, entry.evolutions));
    return { next, done, needed, fraction: done / needed };
  }
  if (entry.progressToNext != null) {
    return { next, done: null, needed, fraction: Math.max(0, Math.min(1, entry.progressToNext)) };
  }
  return { next, done: null, needed, fraction: null };
}

/**
 * The store key for a species' Mega form named by the letter GO prints
 * beside it: "" and "X" are the base Mega, "Y" the extra form. Null when
 * the species has no such form.
 */
export function megaFormKeyForLabel(dexId: number, formLabel: string): string | null {
  const forms = megaFormsForSpecies(dexId);
  if (forms.length === 0) return null;
  const letter = formLabel.trim().toUpperCase();
  if (letter === "Y") return forms.find((f) => f.formLabel === "Y")?.key ?? null;
  return forms[0].key;
}

/** One line per perk, the way the Bonuses page states a bonus. */
export function megaLevelPerkLines(level: MegaLevel): string[] {
  const p = megaLevelInfo(level);
  const out = [
    `+${p.catchCandy} Candy per catch of a Pokémon sharing a type with your Mega`,
    "Boosted attacks for every Pokémon in the raid, more for those sharing its type",
  ];
  if (p.xlChancePct > 0) out.push(`+${p.xlChancePct}% chance of Candy XL on those catches`);
  if (p.catchXp > 0) out.push(`+${p.catchXp} XP on those catches`);
  out.push(`Rest period ${p.restHours >= 48 ? `${p.restHours / 24} days` : `${p.restHours} hours`}`);
  return out;
}

/** How long a Mega Evolution lasts in GO. */
export const MEGA_ACTIVE_MS = 8 * 60 * 60 * 1000;

/** A Mega form the trainer could track: one per species, two for the X/Y pairs. */
export interface MegaFormRef {
  /** Store key: "6" for the base Mega, "6:charizard-mega-y" for an extra form. */
  key: string;
  dexId: number;
  /** Extra-form id (the store's formId), null for the base Mega. */
  formId: string | null;
  /** "Mega Charizard X". */
  name: string;
  /** "" / "X" / "Y", matching the stats feed. */
  formLabel: string;
  stat: MegaStat | null;
}

export function megaFormKey(dexId: number, formId: string | null): string {
  return formId ? `${dexId}:${formId}` : String(dexId);
}

export function parseMegaFormKey(key: string): { dexId: number; formId: string | null } | null {
  const [id, formId] = key.split(":");
  const dexId = Number(id);
  if (!Number.isInteger(dexId) || dexId <= 0) return null;
  return { dexId, formId: formId ?? null };
}

/**
 * Whether the species has a Mega in GO (the Mega dex roster).
 *
 * Resolved through megaGmaxSlug so an admin-added Mega from the dex-config
 * portal counts, exactly as it does for sprite resolution. Reading the
 * bundled table directly is what left portal-added Starmie without a Mega
 * view, without Mega Levels and labelled "Starmie" on event tiles.
 */
/**
 * Primal Reversion (2026-09-09 owner ask): Kyogre #382 and Groudon #383. In
 * Pokémon GO it is a Mega in every mechanic this app models (Mega Energy, the
 * Mega Level circles, the Mega dex cell, Mega raids, the species page's Mega
 * view), so the two ride the Mega roster and tables unchanged. The ONE
 * difference is the word: nothing about them is called "Mega" on screen, so
 * every name and label that would say "Mega X" says "Primal X" instead.
 */
export const PRIMAL_REVERSION_IDS: readonly number[] = [382, 383];
const PRIMALS = new Set<number>(PRIMAL_REVERSION_IDS);

export function isPrimalReversion(dexId: number): boolean {
  return PRIMALS.has(dexId);
}

/** The word in front of the species name: "Mega Gyarados", "Primal Kyogre". */
export function megaNamePrefix(dexId: number): "Mega" | "Primal" {
  return PRIMALS.has(dexId) ? "Primal" : "Mega";
}

export function speciesHasMega(dexId: number): boolean {
  return megaGmaxSlug("mega", dexId) != null;
}

/** Every Mega form of a species, base first, then X/Y extras. Empty without a Mega. */
export function megaFormsForSpecies(dexId: number): MegaFormRef[] {
  if (!speciesHasMega(dexId)) return [];
  const base = speciesName(dexId);
  const baseLabel = megaFormLabel(dexId, null) ?? "";
  const out: MegaFormRef[] = [
    {
      key: megaFormKey(dexId, null),
      dexId,
      formId: null,
      name: `${megaNamePrefix(dexId)} ${base}${baseLabel ? ` ${baseLabel}` : ""}`,
      formLabel: baseLabel,
      stat: megaStatFor(dexId, baseLabel),
    },
  ];
  for (const f of MEGA_EXTRA_FORMS.filter((f) => f.baseId === dexId)) {
    out.push({
      key: megaFormKey(dexId, f.formId),
      dexId,
      formId: f.formId,
      name: `${megaNamePrefix(dexId)} ${base} ${f.label}`,
      formLabel: f.label,
      stat: megaStatFor(dexId, f.label),
    });
  }
  return out;
}

/** Every Mega form in the roster, dex order. */
export function allMegaForms(): MegaFormRef[] {
  return megaRosterBaseIds().flatMap(megaFormsForSpecies);
}

export function megaFormByKey(key: string): MegaFormRef | null {
  const parsed = parseMegaFormKey(key);
  if (!parsed) return null;
  return megaFormsForSpecies(parsed.dexId).find((f) => f.key === key) ?? null;
}

/**
 * A raid-value proxy for ranking a trainer's Megas against each other:
 * attack weighs most, with a little bulk, the way the raid meta rankings
 * come out. Null when the feed has no stats for the form (a pre-release
 * Mega).
 */
export function megaPowerScore(stat: MegaStat | null): number | null {
  if (!stat) return null;
  return Math.round(stat.atk * 1.4 + Math.sqrt(stat.def * stat.sta) * 0.6);
}

/** The Mega forms sorted best-first by the proxy above. */
export function rankMegaForms(forms: readonly MegaFormRef[]): MegaFormRef[] {
  return [...forms].sort(
    (a, b) => (megaPowerScore(b.stat) ?? -1) - (megaPowerScore(a.stat) ?? -1),
  );
}

export { MEGA_STATS };
